import { Box, Checkbox, CircularProgress, FormHelperText, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { callAPIAsync, getOrganisationUsers } from '../../utils/api';
import { opWithView, switchOpSuffix } from '../../utils/operations';
import { Organisation, UserDetails } from '../../types/user';
import { GetOrganisationUsersResponse } from '../../types/api';
import ErrorDisplay from '../ErrorDisplay';
import { CheckBoxOutlineBlank, WarningOutlined } from '@mui/icons-material';
import { hasViewAllPermission } from '../../store/user/userSlice';
import { useLogto } from '@logto/react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

interface UserSelectedProps {
	currentOperation?: string;
	operationType: 'CREATE' | 'MANAGE';
	selectedUploadUsers: Record<string, UserDetails>;
	selectedViewUsers: Record<string, UserDetails>;
	setSelectedUploadUsers: (newUsers: Record<string, UserDetails>) => void;
	setSelectedViewUsers: (newUsers: Record<string, UserDetails>) => void;
}

const commonBoxProps = {
	bgcolor: 'action.selected',
	width: '100%',
	minHeight: '30em',
};

const UserSelect: React.FC<UserSelectedProps> = ({
	currentOperation,
	operationType,
	selectedUploadUsers,
	setSelectedUploadUsers,
	selectedViewUsers,
	setSelectedViewUsers,
}) => {
	const logto = useLogto();

	const userOrganisations = useAppSelector((state) => state.user.orgs);
	const allowedOptions = useAppSelector((state) => state.user.roles);
	const hasViewAll = useAppSelector((state) => hasViewAllPermission(state));

	const [opsLoaded, setOpsLoaded] = useState(false);
	const [errorText, setErrorText] = useState<string | string[]>('');
	const [users, setUsers] = useState<Record<string, UserDetails>>({});

	const rows = Object.entries(users).map(([id, userDetails]) => ({
		id,
		...userDetails,
	}));

	const bothEnabled =
		operationType === 'CREATE' ||
		hasViewAll ||
		(currentOperation && !currentOperation.endsWith('-upload') && !currentOperation.endsWith('-view'));

	const uploadEnabled = bothEnabled || (currentOperation && allowedOptions.includes(currentOperation));

	const viewEnabled =
		bothEnabled || (currentOperation && (currentOperation.endsWith('-view') || allowedOptions.includes(opWithView(currentOperation))));

	useEffect(() => {
		callAPIAsync(logto, async (token) => {
			const responseErrors: string[] = [];
			const receivedUsers: Record<string, UserDetails> = {};

			// check the user has *any* assign permissions
			if (hasViewAll || userOrganisations.some((org) => org.permissions.includes('assign:operation'))) {
				await Promise.all(
					userOrganisations.map(async (userOrg) => {
						// only act on orgs they have the assign permission for
						if (userOrg.permissions.includes('view_all:operation') || userOrg.permissions.includes('assign:operation')) {
							// get all users for all orgs the user controls
							// and their currently assigned "roles" (cases)
							const orgResp = await getOrganisationUsers(token, userOrg.id);
							if (orgResp.ok) {
								const userResp = (await orgResp.json()) as GetOrganisationUsersResponse[];
								userResp.forEach((resp) => {
									if (resp.id in receivedUsers) {
										receivedUsers[resp.id].orgs.push(userOrg);
									} else {
										receivedUsers[resp.id] = {
											name: resp.name,
											email: resp.primaryEmail,
											orgs: [userOrg],
											roles: resp.roles,
										};
									}
								});
							} else {
								responseErrors.push(orgResp.statusText);
							}
						}
					})
				);

				if (responseErrors.length > 0 && Object.keys(receivedUsers).length === 0) {
					setErrorText(['Could not fetch users to assign to operations:', ...responseErrors]);
				}

				setUsers(
					// restructure
					Object.fromEntries(
						// destructure and sort by name
						Object.entries(receivedUsers).sort(([, userA], [, userB]) => {
							return userA.name.localeCompare(userB.name);
						})
					) as Record<string, UserDetails>
				);
			} else {
				setErrorText('You do not have permission to assign any users other than yourself');
			}
			setOpsLoaded(true);
		});
	}, []);

	useEffect(() => {
		const newSelectedUploadUsers: Record<string, UserDetails> = {};
		const newSelectedViewUsers: Record<string, UserDetails> = {};

		if (currentOperation) {
			Object.entries(users).forEach(([id, user]) => {
				if (currentOperation.endsWith('-upload')) {
					if (user.roles.includes(currentOperation)) {
						newSelectedUploadUsers[id] = user;
					}
					if (user.roles.includes(switchOpSuffix(currentOperation))) {
						newSelectedViewUsers[id] = user;
					}
				} else {
					if (user.roles.includes(currentOperation) && operationType !== 'CREATE') {
						newSelectedUploadUsers[id] = user;
						newSelectedViewUsers[id] = user;
					}
				}
			});
		}
		setSelectedUploadUsers(newSelectedUploadUsers);
		setSelectedViewUsers(newSelectedViewUsers);
	}, [users]);

	const handleUploadAssignment = (id: string, user: UserDetails, checked: boolean, legacy?: boolean) => {
		if (checked) {
			setSelectedUploadUsers({
				...selectedUploadUsers,
				[id]: user,
			});
			if (currentOperation) {
				setUsers({
					...users,
					[id]: {
						...users[id],
						roles: [...users[id].roles, currentOperation],
					},
				});
			}
		} else if (id in selectedUploadUsers) {
			const newUsers = { ...selectedUploadUsers };
			delete newUsers[id];
			setSelectedUploadUsers({ ...newUsers });
			if (currentOperation && users[id].roles.includes(currentOperation)) {
				setUsers({
					...users,
					[id]: {
						...users[id],
						roles: users[id].roles.filter((role) => role !== currentOperation),
					},
				});
			}
		}

		if (!legacy && !currentOperation?.endsWith('-upload') && operationType !== 'CREATE') {
			handleViewAssignment(id, user, checked, true);
		}
	};

	const handleViewAssignment = (id: string, user: UserDetails, checked: boolean, legacy?: boolean) => {
		if (checked) {
			setSelectedViewUsers({
				...selectedViewUsers,
				[id]: user,
			});

			if (currentOperation) {
				setUsers({
					...users,
					[id]: {
						...users[id],
						roles: [...users[id].roles, opWithView(currentOperation)],
					},
				});
			}
		} else if (id in selectedViewUsers) {
			const newUsers = { ...selectedViewUsers };
			delete newUsers[id];
			setSelectedViewUsers({ ...newUsers });
			if (currentOperation && users[id].roles.includes(opWithView(currentOperation))) {
				setUsers({
					...users,
					[id]: {
						...users[id],
						roles: users[id].roles.filter((role) => role !== opWithView(currentOperation)),
					},
				});
			}
		}

		if (!legacy && !currentOperation?.endsWith('-upload') && operationType !== 'CREATE') {
			handleUploadAssignment(id, user, checked, true);
		}
	};

	const headCells: GridColDef[] = [
		{
			field: 'name',
			headerName: 'User Name',
			sortable: true,
			flex: 1,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) => (
				<Typography data-testid={`user-select-${params.row.name}`}>{params.row.name}</Typography>
			),
		},
		{
			field: 'email',
			headerName: 'Email',
			sortable: true,
			flex: 1,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) => <Typography data-testid={`user-select-row`}>{params.row.email}</Typography>,
		},
		{
			field: 'orgs',
			headerName: 'Organisations',
			flex: 1,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) =>
				params.row.orgs.length > 0 ? (
					<Stack>
						{params.row.orgs.map((org: Organisation) => {
							return <ListItemText key={`${params.row.email}-${org.name}-cell`} primary={org.name}></ListItemText>;
						})}
					</Stack>
				) : (
					<Typography data-testid={`user-select-row-noorg`}>No Organisations Assigned</Typography>
				),
		},
		{
			field: 'upload',
			headerName: 'Upload Data',
			flex: 0.5,
			display: 'flex',
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title={!viewEnabled ? "You don't have permission to assign users to view data in this operation" : ''}>
					<span>
						<Checkbox
							checked={params.row.id in selectedUploadUsers}
							onChange={(_, checked) => handleUploadAssignment(params.row.id, params.row, checked, false)}
							disabled={!uploadEnabled}
							icon={uploadEnabled ? <CheckBoxOutlineBlank /> : <WarningOutlined />}
							data-testid="user-select-upload-check"
							inputProps={{
								'aria-label': `Give ${params.row.name} Upload Permission`,
							}}
						/>
					</span>
				</Tooltip>
			),
		},
		{
			field: 'view',
			headerName: 'Visualise Data',
			flex: 0.5,
			display: 'flex',
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title={!viewEnabled ? "You don't have permission to assign users to view data in this operation" : ''}>
					<span>
						<Checkbox
							checked={params.row.id in selectedViewUsers}
							onChange={(_, checked) => handleViewAssignment(params.row.id, params.row, checked, false)}
							disabled={!viewEnabled}
							icon={viewEnabled ? <CheckBoxOutlineBlank /> : <WarningOutlined />}
							data-testid="user-select-view-check"
							inputProps={{
								'aria-label': `Give ${params.row.name} View Permission`,
							}}
						/>
					</span>
				</Tooltip>
			),
		},
	];

	// the default: an empty box
	let displayedComponent = <Box {...commonBoxProps} sx={{ opacity: 0.5 }} data-testid="user-select-initial-disabled" />;

	// create does not require the operation to already be selected
	if (currentOperation || operationType === 'CREATE') {
		if (!opsLoaded) {
			displayedComponent = (
				<Box
					{...commonBoxProps}
					flex={1}
					textAlign={'center'}
					alignContent={'center'}
					sx={{ opacity: 0.5 }}
					data-testid="user-select-loading"
				>
					<CircularProgress size={50} aria-label="User Select Loading Spinner" />
					<Typography>Loading Users...</Typography>
				</Box>
			);
		} else {
			if (errorText) {
				displayedComponent = (
					<Box {...commonBoxProps} data-testid="user-select-error">
						<ErrorDisplay summaryText="" errorText={errorText} hideContact />
					</Box>
				);
			} else {
				displayedComponent = (
					<DataGrid
						data-testid={'user-select-table'}
						showToolbar
						key={'manageOperations'}
						rows={rows}
						columns={headCells}
						disableColumnFilter
						localeText={{
							noRowsLabel: 'No users were found',
							paginationRowsPerPage: 'Users per page:',
							columnsManagementSearchTitle: 'Hello!',
						}}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10, page: 0 },
							},
						}}
						sx={{
							'& .MuiDataGrid-row': {
								minHeight: '6ch !important',
							},
							'& .MuiDataGrid-row.Mui-selected': {
								backgroundColor: 'background.paper',
							},
							'& .MuiDataGrid-row.Mui-selected:hover': {
								backgroundColor: 'background.paper',
							},
						}}
						slotProps={{
							toolbar: {
								showQuickFilter: true,
								// Hide other default toolbar buttons if present
								csvOptions: { disableToolbarButton: true },
								printOptions: { disableToolbarButton: true },
							},
						}}
						disableColumnSelector
						getRowHeight={() => 'auto'}
						autoHeight
						disableRowSelectionOnClick
						disableColumnMenu
						pageSizeOptions={[10, 25, 50]}
					/>
				);
			}
		}
	}

	return (
		<Box width="100%">
			<Typography variant="h6" component="h2" sx={{ padding: 1 }} textAlign={'center'}>
				Select Users to Assign
			</Typography>
			{displayedComponent}

			<FormHelperText sx={{ paddingTop: 1 }}>
				This list is generated from the organisations you have operation assignment permissions in
			</FormHelperText>
			<FormHelperText>If no users are granted upload and/or view permissions, you will be automatically assigned to both</FormHelperText>
		</Box>
	);
};

export default UserSelect;
